import { useEffect, useState, useCallback } from "react";

/** Fired when sessionStorage.featuresList is updated (e.g. after useDynamicMenu loads). */
export const FEATURES_LIST_UPDATED_EVENT = "features-list-updated";

function getFeatureName(item) {
  return item?.feature_name ?? item?.featureName ?? item?.FEATURE_NAME ?? "";
}

function isTruthyPermissionFlag(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

function isFeatureRowActive(feature) {
  if (!feature) return false;
  return (
    feature.is_active !== false &&
    feature.isActive !== false &&
    feature.is_active !== 0
  );
}

/** True only when the role row grants access (view + active). */
function isFeatureEnabledForRole(feature) {
  if (!feature) return false;
  const canView =
    isTruthyPermissionFlag(feature.can_view) || isTruthyPermissionFlag(feature.canView);
  return Boolean(canView && isFeatureRowActive(feature));
}

function readFeaturePermissions(feature) {
  if (!feature || !isFeatureRowActive(feature)) {
    return { canView: false, canInsert: false, canUpdate: false };
  }
  return {
    canView:
      isTruthyPermissionFlag(feature.can_view) || isTruthyPermissionFlag(feature.canView),
    canInsert:
      isTruthyPermissionFlag(feature.can_insert) || isTruthyPermissionFlag(feature.canInsert),
    canUpdate:
      isTruthyPermissionFlag(feature.can_update) || isTruthyPermissionFlag(feature.canUpdate),
  };
}

/**
 * Returns whether the named master feature is enabled for the current role
 * (reads sessionStorage.featuresList from userrolefeaturepermission).
 */
function useFeaturePermission(featureName) {
  const [enabled, setEnabled] = useState(false);

  const recompute = useCallback(() => {
    const featureList = JSON.parse(
      sessionStorage.getItem("featuresList") || "[]"
    );
    const feature = featureList.find(
      (item) => getFeatureName(item) === featureName
    );
    setEnabled(isFeatureEnabledForRole(feature));
  }, [featureName]);

  useEffect(() => {
    recompute();
    const onListUpdated = () => recompute();
    window.addEventListener(FEATURES_LIST_UPDATED_EVENT, onListUpdated);
    const onVisible = () => {
      if (document.visibilityState === "visible") recompute();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener(FEATURES_LIST_UPDATED_EVENT, onListUpdated);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [recompute]);

  return enabled;
}

/**
 * Returns dynamic master-feature permissions for the current role
 * (reads sessionStorage.featuresList from userrolefeaturepermission).
 */
export function useFeaturePermissions(featureName) {
  const [permissions, setPermissions] = useState({
    canView: false,
    canInsert: false,
    canUpdate: false,
  });

  const recompute = useCallback(() => {
    const featureList = JSON.parse(sessionStorage.getItem("featuresList") || "[]");
    const feature = featureList.find((item) => getFeatureName(item) === featureName);
    setPermissions(readFeaturePermissions(feature));
  }, [featureName]);

  useEffect(() => {
    recompute();
    const onListUpdated = () => recompute();
    window.addEventListener(FEATURES_LIST_UPDATED_EVENT, onListUpdated);
    const onVisible = () => {
      if (document.visibilityState === "visible") recompute();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener(FEATURES_LIST_UPDATED_EVENT, onListUpdated);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [recompute]);

  return permissions;
}

export default useFeaturePermission;
